import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'

interface PaginationProps {
  currentPage: number
  totalPages: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
}

export default function Pagination({ currentPage, totalPages, pageSize: _pageSize, totalCount: _totalCount, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  // Build page numbers with ellipsis
  const pages: (number | '...')[] = []
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i)
  } else {
    pages.push(0)
    if (currentPage > 2) pages.push('...')
    const start = Math.max(1, currentPage - 1)
    const end = Math.min(totalPages - 2, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 3) pages.push('...')
    pages.push(totalPages - 1)
  }

  return (
    <nav className="flex items-center justify-between border-t border-gray-200 px-4 dark:border-zinc-700 sm:px-0">
      {/* Previous */}
      <div className="-mt-px flex w-0 flex-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          className="inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-40 disabled:hover:border-transparent dark:text-zinc-400 dark:hover:border-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLongLeftIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 dark:text-zinc-500" />
          Previous
        </button>
      </div>
      {/* Page numbers */}
      <div className="hidden md:-mt-px md:flex">
        {pages.map((page, i) =>
          page === '...' ? (
            <span
              key={`dots-${i}`}
              className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 dark:text-zinc-500"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? 'page' : undefined}
              className={clsx(
                'inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium',
                page === currentPage
                  ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-400 dark:hover:border-zinc-500 dark:hover:text-zinc-300',
              )}
            >
              {page + 1}
            </button>
          ),
        )}
      </div>
      {/* Next */}
      <div className="-mt-px flex w-0 flex-1 justify-end">
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages - 1}
          className="inline-flex items-center border-t-2 border-transparent pt-4 pl-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 disabled:opacity-40 disabled:hover:border-transparent dark:text-zinc-400 dark:hover:border-zinc-500 dark:hover:text-zinc-300"
        >
          Next
          <ArrowLongRightIcon aria-hidden="true" className="ml-3 size-5 text-gray-400 dark:text-zinc-500" />
        </button>
      </div>
    </nav>
  )
}
